<template>
  <div
      class="fields-container"
      v-bind:style="{ height: height + 'px' }"
  >
    <v-virtual-scroll
        :bench="3"
        :height="height"
        :items="fieldListComputed"
        item-height="64"
        width="100%"
    >
      <template v-slot:default="{ item, index }">
          <div
            v-bind:key="'field_' + index "
            class="field-row"
          >
            <div v-if="componentExists(item)">
              <component
                  v-bind:is="getComponentName(item)"
                  v-model="incidentData[item.field]"
                  :label="item.name"
                  :field="item"
                  :clientId="clientId"
              ></component>
            </div>
            <i-o-vascular-access-location
                v-else-if="item.field === 'TD_IOVascularAccessLocation'"
                v-model="incidentData[item.field]"
                :label="item.name"
                :field="item"
                :io-site="incidentData['TD_IOSite']"
            />
            <num-value-pn
                v-else-if="item.type === 'integer-pn' && Array.isArray(item.field)"
                v-model="incidentData[item.field[0]]"
                :pn.sync="incidentData[item.field[1]]"
                :label="item.name"
                :field="item"
            />
            <dbp-pn
                v-else-if="item.type === 'dbp-pn' && Array.isArray(item.field)"
                v-model="incidentData[item.field[0]]"
                :pn.sync="incidentData[item.field[1]]"
                :label="item.name"
                :field="item"
            />
            <date-time
                v-else-if="item.type === 'datetime' && Array.isArray(item.field)"
                :date.sync="incidentData[item.field[0]]"
                :time.sync="incidentData[item.field[1]]"
                :label="item.name"
                :field="item"
            />
            <date-time-single-value
                v-else-if="item.type === 'datetime' && typeof item.field === 'string'"
                :datetime.sync="incidentData[item.field]"
                :label="item.name"
                :field="item"
            />
            <div v-else-if="item.type === 'non-generic-fields'">
              <component
                  v-bind:is="item.field"
                  :incident-data.sync="incidentData"
                  :field="item"
              ></component>
            </div>
            <span v-else-if="item.type === 'gag'"></span>
            <span v-else>{{ item.name }} - Is not supported yet.</span>
          </div>
        </template>
      </v-virtual-scroll>
  </div>
</template>

<script>
import StringControl from '@/components/IncidentView/EditControls/String';
import StringTextarea from '@/components/IncidentView/EditControls/StringTextarea';
import Select from '@/components/IncidentView/EditControls/Select';
import YesValue from '@/components/IncidentView/EditControls/YesValue';
import YesNoValue from '@/components/IncidentView/EditControls/YesNoValue';
import NumValue from '@/components/IncidentView/EditControls/NumValue';
import Date from '@/components/IncidentView/EditControls/Date';
import Time from '@/components/IncidentView/EditControls/Time';
import DateTime from '@/components/IncidentView/EditControls/DateTime';
import DateTimeSingleValue from '@/components/IncidentView/EditControls/DateTimeSingleValue';
import NoBill from '@/components/IncidentView/EditControls/NoBill';
import Joules from '@/components/IncidentView/EditControls/Joules';
import BloodGlucose from '@/components/IncidentView/EditControls/BloodGlucose';
import IncidentNumber from '@/components/IncidentView/EditControls/IncidentNumber';
import Zipcode from '@/components/IncidentView/EditControls/Zipcode';
import TherapiesFields from '@/components/IncidentView/EditNonGenericFields/TherapiesFields';
import IOVascularAccessLocation from '@/components/IncidentView/EditControls/IOVascularAccessLocation';
import TeamMember from '@/components/IncidentView/EditControls/TeamMember';
import NumValuePn from '@/components/IncidentView/EditControls/NumValuePn';
import DbpPn from '@/components/IncidentView/EditControls/DbpPn';
import {
  FETCH_EMT_LIST,
} from '@/store/IncidentView/actions';
import { mapActions } from 'vuex';

export default {
  name: 'EditCategoryFields',

  components: {
    DateTime,
    String: StringControl,
    DateTimeSingleValue,
    StringTextarea,
    Select,
    YesValue,
    YesNoValue,
    Time,
    Date,
    NumValue,
    NoBill,
    Joules,
    BloodGlucose,
    IncidentNumber,
    Zipcode,
    non_generic_therapies: TherapiesFields,
    IOVascularAccessLocation,
    TeamMember,
    NumValuePn,
    DbpPn,
  },
  props: {
    height: {
      type: Number,
      required: true,
    },
    fieldList: {
      type: Array,
      required: true,
    },
    clientId: {
      type: Number,
      required: true,
    },
    incidentData: {
      type: Object,
    },
    categorySlug: {
      type: String,
    },
  },
  computed: {
    fieldListComputed() {
      const fields = [];
      const nonGenericFieldComponentName = `non_generic_${this.categorySlug}`;
      if (this.categorySlug && Object.prototype.hasOwnProperty.call(
        this.$options.components,
        nonGenericFieldComponentName,
      )) {
        fields.push({
          field: nonGenericFieldComponentName,
          type: 'non-generic-fields',
          name: '',
        });
      }
      this.fieldList.forEach(field => {
        fields.push(field);
        if (this.getComponentName(field) === 'StringTextarea') {
          for (let i = 1; i <= 2; i++) {
            fields.push({
              field: `text_area_gag_${i}`,
              type: 'gag',
              name: '',
            });
          }
        }
      });
      return fields;
    },
  },
  mounted() {
    // Try to load cache of EMT list
    if (this.clientId) {
      this.fetchEmtList(this.clientId);
    }
  },
  methods: {
    ...mapActions({
      fetchEmtList: `incidentView/${FETCH_EMT_LIST}`,
    }),
    getComponentName(field) {
      const numTypes = [
        'decimal',
        'integer',
        'weight',
        'age',
      ];
      const typeControlMap = {
        string: 'String',
        date: 'Date',
        time: 'Time',
        joules: 'Joules',
        'blood-glucose': 'BloodGlucose',
        YesValue: 'YesValue',
        YesNoValue: 'YesNoValue',
      };

      // Rendered by a dedicated control (filters options by the selected
      // IO Site), handled outside the generic component path below.
      if (field.field === 'TD_IOVascularAccessLocation') {
        return null;
      }
      if (field.type === 'string' && field.values) {
        return 'Select';
      }
      if (field.field === 'II_IncidentZipcode' || field.field === 'PI_PatientZipcode') {
        return 'Zipcode';
      }
      if (field.field === 'II_IncidentNumber') {
        return 'IncidentNumber';
      }
      // Check typeof to prevent error `TypeError: e.field?.startsWith is not a function`
      if (typeof field.field === 'string' && field.field.startsWith('TM_TeamMemberID')) {
        return 'TeamMember';
      }

      if (field.field === 'PI_NoBill') {
        return 'NoBill';
      }
      if (field.field === 'comments_comments') {
        return 'StringTextarea';
      }
      if (numTypes.includes(field.type)) {
        return 'NumValue';
      }
      if (Object.prototype.hasOwnProperty.call(typeControlMap, field.type)) {
        return typeControlMap[field.type];
      }

      return null;
    },
    componentExists(field) {
      const componentName = this.getComponentName(field);
      if (!componentName) {
        return false;
      }
      return Object.prototype.hasOwnProperty.call(
        this.$options.components,
        componentName,
      );
    },
  },
};
</script>

<style lang="scss">
.fields-container {
  overflow-y: auto;
  overflow-x: hidden;
  .field-name {
    color: #9a9a9a;
  }
  .field-row {
    width: 90%;
    margin: 0 auto;
  }
}
</style>
